import { useState, useEffect } from 'react';
import { ticketApi } from '../../api/ticket.api';
import { useWorkspaceNav } from '../../store/WorkspaceContext';
import { Badge, PageHeader, EmptyState, Skeleton } from '../../components/ui/index';
import { toPersian, timeAgo } from '../../utils/persianNumber';
import TicketFilterBar from '../../components/tickets/TicketFilterBar';
import { useTicketFilters } from '../../hooks/useTicketFilters';
import TicketBodyPreview from '../../components/tickets/TicketBodyPreview';

// manager_employee-only: tickets they've personally referred to a
// sub-employee (via TicketProcessPage's "ارجاع به کارمند زیرمجموعه").
// Separate from their own ticket queue, since a referred-out ticket no
// longer shows up there once handed off (see queryGetTicketListView).
export default function ReferredOutTicketsPage() {
  const workspaceNav = useWorkspaceNav();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    ticketApi.getReferredOutTickets()
      .then((res) => setTickets(Array.isArray(res.data) ? res.data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Sorted by refer_date (when it was handed off), not send_date - that's
  // the meaningful timestamp on this particular list.
  const { filteredSorted: sortedTickets, company, setCompany, sortBy, setSortBy, companyOptions } =
    useTicketFilters(tickets, { companyKey: 'company', dateKey: 'refer_date' });

  return (
    <div className="fade-in">
      <PageHeader title="تیکت‌های ارجاع‌شده به زیرمجموعه" subtitle={`${toPersian(tickets.length)} تیکت`} />

      {tickets.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <TicketFilterBar
            sortBy={sortBy} setSortBy={setSortBy}
            company={company} setCompany={setCompany}
            companyOptions={companyOptions}
            sortOptions={[
              { value: 'date_desc', label: 'جدیدترین ارجاع' },
              { value: 'date_asc', label: 'قدیمی‌ترین ارجاع' },
              { value: 'company_asc', label: 'نام شرکت (الفبا)' },
            ]}
          />
        </div>
      )}

      {loading
        ? <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{[1,2,3].map((k) => <SkeletonRow key={k} />)}</div>
        : tickets.length === 0
          ? <EmptyState icon="↩️" title="موردی یافت نشد" subtitle="تیکتی به کارمند زیرمجموعه ارجاع نداده‌اید" />
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {sortedTickets.map((t) => (
                <div key={t.id} className="pj-row-card pj-card-hover" style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, cursor: 'pointer', transition: 'all 0.15s' }}
                  onClick={() => workspaceNav(`/tickets/${t.id}/process`, t.title || 'تیکت')}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = '#bfdbfe'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                >
                  <div style={{ minWidth: 0 }}>
                    <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '0 0 4px' }}>{t.title}</h3>
                    <TicketBodyPreview ticket={t} />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', fontSize: 12, color: '#64748b' }}>
                      <span>↩️ ارجاع به {[t.employee_first_name, t.employee_last_name].filter(Boolean).join(' ') || t.employee_username}</span>
                      {t.company && <span>🏢 {t.company_display || t.company}</span>}
                      <span style={{ color: '#94a3b8' }}>🕒 {timeAgo(t.refer_date)}</span>
                    </div>
                  </div>
                  <Badge variant={t.responsed ? 'green' : 'amber'}>
                    {t.responsed ? '✓ پاسخ داده شده' : 'در انتظار پاسخ'}
                  </Badge>
                </div>
              ))}
            </div>
          )
      }
    </div>
  );
}

function SkeletonRow() {
  return <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e2e8f0' }}><Skeleton h={18} w="50%" style={{ marginBottom: 8 }} /><Skeleton h={14} w="30%" /></div>;
}
